import React, { memo, type MutableRefObject } from 'react';

import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { SelectionToolbarPlugin } from '@atlaskit/editor-plugins/selection-toolbar';

import { AgenticEditorBannerLoader } from '@confluence/editor-action-bar/entry-points/AgenticEditorBannerLoader.ts';
import { EditorControlsBanner } from '@confluence/editor-action-bar/entry-points/EditorControlsBanner.ts';
import { SmartCreateFeedbackBannerLoader } from '@confluence/editor-action-bar/entry-points/SmartCreateFeedbackBannerLoader.ts';
import { useIsControlsBannerDismissed } from '@confluence/editor-action-bar/entry-points/useIsControlsBannerDismissed.ts';
import { useOnboardingBannerLogic } from '@confluence/onboarding-editor';
import type { TriggerAnalyticsEventType } from '@confluence/editor-features/entry-points/TriggerAnalyticsEventType.ts';
import { fg } from '@confluence/feature-gating';
import { ExcludeNewConfluenceActiveUser } from '@confluence/targeting';

type BaseProps = {
	isLivePage: boolean;
	isEmbeddedConfluence: boolean;
	viewMode?: boolean;
	spaceKey: string;
	editorFullPagePresetAPI: PublicPluginAPI<[SelectionToolbarPlugin]> | undefined;
	triggerAnalyticsEvent?: TriggerAnalyticsEventType;
	contentId: string;
	version: number;
	isShowingOfflineBanner: boolean;
};

export type FullPageEditorBannerWrapperProps = BaseProps & {
	isAgenticMode: boolean;
	onExitAgenticMode: () => void;
	agenticModeStartTime?: number;
	// Pass refs (not values) so the banner reads live counts at submit time.
	aiInvocationCountRef?: MutableRefObject<number>;
	aiInvocationSurfacesRef?: MutableRefObject<Record<string, number>>;
};

const StandardBannerWrapper = ({
	isLivePage,
	isEmbeddedConfluence,
	viewMode,
	spaceKey,
	triggerAnalyticsEvent,
	contentId,
	version,
	isShowingOfflineBanner,
}: BaseProps) => {
	const { isControlsBannerDismissed, onDismiss, bannerType } =
		useIsControlsBannerDismissed(isLivePage);

	const { isVisible: isOnboardingEditorDraftBannerVisible } = useOnboardingBannerLogic({
		contentId,
		version,
		isShowingOfflineBanner,
		spaceKey,
		setIsOnboardingBannerDataLoaded: () => {},
	});
	const shouldShowControlsBanner =
		!viewMode &&
		!isControlsBannerDismissed &&
		spaceKey !== 'XFLOW' &&
		!isOnboardingEditorDraftBannerVisible &&
		!fg('platform_editor_control_remove_banner'); // remove change boarding banner for all customers. Remove the EditorControlsBanner completely when cleaning up the flag.

	if (shouldShowControlsBanner) {
		return (
			<ExcludeNewConfluenceActiveUser>
				<EditorControlsBanner
					onDismiss={onDismiss}
					triggerAnalyticsEvent={triggerAnalyticsEvent}
					isLivePage={isLivePage}
					isEmbeddedConfluence={isEmbeddedConfluence}
					bannerType={bannerType}
				/>
			</ExcludeNewConfluenceActiveUser>
		);
	}

	if (spaceKey !== 'XFLOW' && !isLivePage && fg('aifc_create_enabled')) {
		return <SmartCreateFeedbackBannerLoader contentType="page" />;
	}

	return null;
};

export const FullPageEditorBannerWrapper = memo(
	({
		isAgenticMode,
		onExitAgenticMode,
		agenticModeStartTime,
		aiInvocationCountRef,
		aiInvocationSurfacesRef,
		contentId,
		spaceKey,
		...standardProps
	}: FullPageEditorBannerWrapperProps) => {
		if (isAgenticMode) {
			return (
				<AgenticEditorBannerLoader
					onExitAgenticMode={onExitAgenticMode}
					agenticModeStartTime={agenticModeStartTime}
					aiInvocationCountRef={aiInvocationCountRef}
					aiInvocationSurfacesRef={aiInvocationSurfacesRef}
				/>
			);
		}

		return <StandardBannerWrapper contentId={contentId} spaceKey={spaceKey} {...standardProps} />;
	},
);
